import React, {useEffect, useRef, useState} from "react";
import moment from "moment/moment";
import {fetchTVShowDetails} from "../utilities/dataSources";
import {ProgrammeModal} from "./ProgrammeModal";
import {StartEndTime, ShowTime} from "../TVGuide";

type Programme = StartEndTime & { name: string; UniqueEventID: string };

function getProgrammes(tvGuide: any, channel: string, date: string): Programme[] {
    const programDictionary = tvGuide['ProgramMapping'];
    const programmes: Programme[] = [];
    const channelData = tvGuide[date]['Channels'].find((item: any) => item.Number === channel);
    if (channelData) {
        channelData['Programmes'].forEach((prog: any) => {
            programmes.push({
                name: programDictionary[prog.TitleId],
                startTime: prog.StartTime,
                endTime: prog.EndTime,
                UniqueEventID: prog.UniqueEventID
            });
        });
    }
    return programmes;
}

function GameModal({programmes, onClose}: { programmes: Programme[]; onClose: () => void }) {
    const [timer, setTimer] = useState(30);
    const [score, setScore] = useState(0);
    const [clickedIds, setClickedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (timer <= 0) return;
        const interval = setInterval(() => setTimer(t => t - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    const handleClick = (id: string) => {
        if (timer <= 0 || clickedIds.has(id)) return;
        setScore(s => s + 1);
        setClickedIds(prev => new Set(prev).add(id));
    };

    const timeUp = timer <= 0;

    return (
        <div className="modal-backdrop">
            <div className="modal text-theme-color">
                <i className="modal-close-btn fas fa-times" onClick={onClose}></i>
                <h2>{timeUp ? "Time's Up!" : "30-Second Click Game"}</h2>
                <p>
                    <strong>Time:</strong> {timer}s &nbsp;|&nbsp; <strong>Score:</strong> {score}
                </p>
                {timeUp && <p>Nice work! You clicked {score} programme{score !== 1 ? 's' : ''}.</p>}
                <div className="game-grid">
                    {programmes.map(prog => (
                        <button
                            key={prog.UniqueEventID}
                            onClick={() => handleClick(prog.UniqueEventID)}
                            disabled={timeUp || clickedIds.has(prog.UniqueEventID)}
                        >
                            {prog.name}
                        </button>
                    ))}
                </div>
                <button className="modal-btn modal-btn-primary" onClick={onClose}>
                    {timeUp ? 'Close' : 'Quit'}
                </button>
            </div>
        </div>
    );
}

export function ChannelsView({tvGuide, country, onSwitchToProgrammes}: {
    tvGuide: any;
    country: "south-africa" | "nigeria";
    onSwitchToProgrammes?: () => void;
}) {
    type Channel = { name: string; number: string };

    const [dates, setDates] = useState<{ weekDay: string; date: string }[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [channels, setChannels] = useState<Channel[]>([]);
    const [selectedChannel, setSelectedChannel] = useState<string>('');
    const [showGame, setShowGame] = useState(false);
    const [gameProgs, setGameProgs] = useState<Programme[]>([]);
    const [selectedProgrammeData, setSelectedProgrammeData] = useState<any>(null);
    const [selectedShowTime, setSelectedShowTime] = useState<ShowTime | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    let minDate = useRef<string>('');
    let maxDate = useRef<string>('');

    useEffect(() => {
        const available = Object.keys(tvGuide)
            .filter(key => key.match(/\d{4}-\d{2}-\d{2}/))
            .filter(key => tvGuide[key]['Channels'].length > 0)
            .map(key => moment(key, 'YYYY-MM-DD'))
            .map(d => ({ weekDay: d.format('ddd'), date: d.format('YYYY-MM-DD') }));
        setDates(available);
        setSelectedDate(available[0]?.date || '');
        minDate.current = available[0]?.date || '';
        maxDate.current = available[available.length - 1]?.date || '';
    }, [tvGuide]);

    useEffect(() => {
        if (!selectedDate) return;
        const ch = tvGuide[selectedDate]['Channels'].map((item: any) => ({ name: item.Name, number: item.Number }));
        setChannels(ch);
    }, [selectedDate, tvGuide]);

    const updateSelectedChannel = (num: string) => {
        setSelectedChannel(prev => prev === num ? '' : num);
    };

    const onDiceClick = (num: string) => {
        updateSelectedChannel(num);
        const progs = getProgrammes(tvGuide, num, selectedDate);
        setGameProgs(progs);
        setShowGame(true);
    };

    const isCurrentlyPlaying = (date: string, startTime: string, endTime: string): boolean => {
        const [y, mo, d] = date.split('-').map(Number);
        const [sh, sm] = startTime.split(':').map(Number);
        const [eh, em] = endTime.split(':').map(Number);

        const start = new Date(y!, mo! - 1, d!, sh!, sm!, 0, 0);
        let end = new Date(y!, mo! - 1, d!, eh!, em!, 0, 0);

        if (end.getTime() <= start.getTime()) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        }

        const now = new Date();
        return now >= start && now <= end;
    };

    const clickNext = ()=> {
        if (!selectedDate || selectedDate >= maxDate.current) return;
        const momentDate = moment(selectedDate, 'YYYY-MM-DD');
        const nextDate = momentDate.add(1, 'day').format('YYYY-MM-DD');
        setSelectedDate(nextDate);
    };

    const clickPrev = () => {
        if (!selectedDate || selectedDate <= minDate.current) return;
        const momentDate = moment(selectedDate, 'YYYY-MM-DD');
        const prevDate = momentDate.subtract(1, 'day').format('YYYY-MM-DD');
        setSelectedDate(prevDate);
    };

    const fetchProgrammeDetails = async (prog: Programme, channelNumber: string, channelName: string) => {
        setDetailsLoading(true);
        try {
            const response = await fetchTVShowDetails(country, prog.UniqueEventID);
            if (response.data.Duration) {
                setSelectedProgrammeData(response.data);
                setSelectedShowTime({
                    dateYYYMMDD: selectedDate,
                    date: `${selectedDate.slice(8)}-${moment(selectedDate).format("ddd")}`,
                    startTime: prog.startTime,
                    endTime: prog.endTime,
                    channelNumber,
                    channelName,
                    uniqueEventId: prog.UniqueEventID,
                });
            } else {
                console.warn("No duration found for this programme:", response.data);
                setSelectedProgrammeData(null);
                setSelectedShowTime(null);
                alert("Synopsis could not be fetched for this programme.");
            }
        } catch (err) {
            console.error(err);
            setSelectedProgrammeData(null);
            setSelectedShowTime(null);
            alert("Programme details could not be loaded. Please try again.");
        } finally {
            setDetailsLoading(false);
        }
    };

    return (
        <>
            <input
                className="filterTextBox"
                placeholder="Search programmes…"
                onFocus={() => onSwitchToProgrammes?.()}
            />

            {dates.length > 0 && (
                <div className="dates">
                    {dates.map(d => (
                        <div
                            key={d.date}
                            className={`date ${selectedDate === d.date ? 'selectedDate' : ''}`}
                            onClick={() => setSelectedDate(d.date)}
                        >{d.weekDay}</div>
                    ))}
                </div>
            )}

            <ul className="channels-view">
                {channels.map(ch => (
                    <li key={ch.number} className="channelItem">
                        <div className="channel-row" onClick={() => updateSelectedChannel(ch.number)}>
                            <span className="channel-number">{ch.number}</span>
                            <span className="channel-name">{ch.name}</span>
                            <span className="dice-btn" onClick={(e) => { e.stopPropagation(); onDiceClick(ch.number); }} title="Play 30s game">🎲</span>
                        </div>

                        {selectedChannel === ch.number && !showGame && (
                            <div className="channelProgramming">
                                <div className="navigation">
                                    <span onClick={_=>clickPrev()} title="Previous Day">&larr; Prev</span>
                                    <span style={{color: 'var(--text-1)', cursor: 'default'}}>{moment(selectedDate).format('ddd, DD MMM')}</span>
                                    <span onClick={_=> clickNext()} title="Next Day">Next &rarr;</span>
                                </div>
                                {getProgrammes(tvGuide, ch.number, selectedDate).map(p => {
                                    const live = isCurrentlyPlaying(selectedDate, p.startTime, p.endTime);
                                    return (
                                        <div key={p.UniqueEventID} className={`channelShowAtTime ${live ? 'current-time-highlight' : ''}`}
                                             onClick={() => fetchProgrammeDetails(p, ch.number, ch.name)} title="View programme details">
                                            <span className="startTime">{p.startTime}</span>
                                            <span className="endTime">{p.endTime}</span>
                                            <span className="programmeName">{p.name}</span>
                                            {live && <span className="live-badge">Live</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {showGame && (
                <GameModal
                    programmes={gameProgs}
                    onClose={() => setShowGame(false)}
                />
            )}

            {detailsLoading && (
                <div className="modal-backdrop details-loading">
                    <div className="pc-spinner" />
                </div>
            )}

            {selectedProgrammeData && (
                <ProgrammeModal
                    data={selectedProgrammeData}
                    showTime={selectedShowTime}
                    country={country}
                    onClose={() => setSelectedProgrammeData(null)}
                />
            )}
        </>
    );
}
